
import React, { useState, useEffect, ReactNode } from 'react';
import Keycloak from 'keycloak-js';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

interface KeycloakContextProps {
  keycloak: Keycloak;
  authenticated: boolean;
  userInfo: any | null;
  roles: string[];
}

const KeycloakContext = React.createContext<KeycloakContextProps | undefined>(undefined);

export const KeycloakProvider = ({ children }: { children: ReactNode}) => {
  const [keycloakInstance, setKeycloakInstance] = useState<Keycloak | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState<any | null>(null);
  const [roles, setRoles] = useState<string[]>([]);

  useEffect(() => {
    const initializeKeycloak = async () => {
      const response = await fetch('/api/settings/keycloak');
      const keycloakSettings = await response.json();

      console.log('Keycloak settings', keycloakSettings);
      const keycloak = new Keycloak(keycloakSettings);

      keycloak.init({ onLoad: 'check-sso' }).then(authenticated => {
        setKeycloakInstance(keycloak);
        setAuthenticated(authenticated);
        if (authenticated) {
          keycloak.loadUserInfo().then((userInfo) => {
            setUserInfo(userInfo);
          }).catch(error => {
            console.error('Failed to load user profile', error);
          });
        }
      }).catch(error => {
        console.error('Failed to initialize Keycloak', error);
      });

      keycloak.onAuthSuccess = () => {
        if (keycloak.token) localStorage.setItem('kc_token', keycloak.token);
        if (keycloak.refreshToken) localStorage.setItem('kc_refreshToken', keycloak.refreshToken);
        setRoles(keycloak?.tokenParsed?.resource_access?.['web-app']?.roles || []);
      };

      keycloak.onAuthRefreshSuccess = () => {
        if (keycloak.token) localStorage.setItem('kc_token', keycloak.token);
        if (keycloak.refreshToken) localStorage.setItem('kc_refreshToken', keycloak.refreshToken);
        setRoles(keycloak?.tokenParsed?.resource_access?.['web-app']?.roles || []);
      };

      keycloak.onAuthLogout = () => {
        localStorage.removeItem('kc_token');
        localStorage.removeItem('kc_refreshToken');
      };

      keycloak.onTokenExpired = () => {
        keycloak.updateToken().then(refreshed => {
          if (refreshed) {
            if (keycloak.token) localStorage.setItem('kc_token', keycloak.token);
            if (keycloak.refreshToken) localStorage.setItem('kc_refreshToken', keycloak.refreshToken);
          } else {
            console.warn('Token is still valid');
          }
        }).catch((e) => {
          console.error('Failed to refresh token', e);
          keycloak.logout();
        });
      };
    }

    initializeKeycloak();
  }, []);

  if (!keycloakInstance) {
    return <LoadingSpinner />;
  }

  return (
    <KeycloakContext.Provider value={{ keycloak: keycloakInstance, authenticated, userInfo, roles }}>
      {children}
    </KeycloakContext.Provider>
  );
};

export const useKeycloak = (): KeycloakContextProps => {
  const context = React.useContext(KeycloakContext);
  if (!context) {
    throw new Error('useKeycloak must be used within a KeycloakProvider');
  }
  return context;
};
